import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Mail, Lock } from 'lucide-react';
import { LanguageSelector, CurrencySelector } from '../components/LocaleSelectors';

export default function CustomerLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success(t('welcomeBack') + '!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-warm-sand flex items-center justify-center px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="w-full max-w-md">
        {/* Locale Selectors */}
        <div className={`flex justify-end gap-2 mb-4 ${isRTL ? 'justify-start' : ''}`}>
          <LanguageSelector />
          <CurrencySelector />
        </div>

        {/* Back Link */}
        <Link 
          to="/" 
          className={`inline-flex items-center text-deep-charcoal/60 hover:text-terracotta font-body text-sm mb-8 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
          data-testid="back-to-home"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
          {t('home')}
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-bold text-terracotta">FTASM</h1>
          <p className="font-body text-deep-charcoal/60 mt-2">{t('welcomeBack')}</p>
        </div>

        {/* Login Card */}
        <Card className="bg-white rounded-3xl shadow-soft border-0">
          <CardContent className="p-8">
            <h2 className="font-heading text-2xl font-semibold text-deep-charcoal mb-6">
              {t('signIn')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body text-sm text-deep-charcoal/70">
                  {t('emailAddress')}
                </Label>
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-deep-charcoal/30 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`rounded-xl border-deep-charcoal/20 h-12 ${isRTL ? 'pr-12' : 'pl-12'}`}
                    placeholder="your@email.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-body text-sm text-deep-charcoal/70">
                  {t('password')}
                </Label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-deep-charcoal/30 ${isRTL ? 'right-4' : 'left-4'}`} />
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`rounded-xl border-deep-charcoal/20 h-12 ${isRTL ? 'pr-12' : 'pl-12'}`}
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl h-12 bg-terracotta text-white hover:bg-terracotta-dark"
                data-testid="login-submit"
              >
                {loading ? t('signingIn') : t('signIn')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="font-body text-sm text-deep-charcoal/60">
                {t('dontHaveAccount')}{' '}
                <Link to="/register" className="text-terracotta hover:underline font-medium" data-testid="go-to-register">
                  {t('signUp')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Staff Portal Link */}
        <div className="mt-6 text-center">
          <Link 
            to="/staff/login" 
            className="font-body text-sm text-deep-charcoal/50 hover:text-terracotta transition-colors"
            data-testid="staff-portal-link"
          >
            {t('staffPortal')} →
          </Link>
        </div>
      </div>
    </div>
  );
}
